/**
 * Who owns a key: a text field, or the page.
 *
 * While a text field has the keyboard (a page input or textarea, a contenteditable, a <select>, or the
 * hidden <input> or <textarea> that backs a field or code editor sleepy-ui draws), every caret-movement key moves its caret or selection and no page
 * hotkey, seek, slide step or table navigation may act on it or cancel it. Every window / document keydown listener
 * that binds a caret key (arrows, Home / End, PageUp / PageDown) asks `isTextEntryTarget(event.target)` first (or
 * `pageMayTakeKey(event)`, the same question with the keys named).
 */

/** The keys that move a caret or a selection in a text field. */
export const CARET_KEYS: ReadonlySet<string> = new Set([
  'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown',
]);

/** Input types that take no text: a checkbox, a slider ... keep the page's keys. */
const NON_TEXT_INPUT = /^(button|checkbox|radio|submit|reset|file|range|color|image)$/i;

/** Whether `key` (KeyboardEvent.key) moves a caret. */
export function isCaretKey(key: string): boolean {
  return CARET_KEYS.has(key);
}

/** Whether keys sent to `target` are typing: an input that takes text, a textarea, a select, an editable region. */
export function isTextEntryTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.closest !== 'function') return false;
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') return !NON_TEXT_INPUT.test((el as HTMLInputElement).type || 'text');
  if (el.isContentEditable) return true;
  return !!el.closest('[contenteditable=""], [contenteditable="true"], [contenteditable="plaintext-only"], [role="textbox"]');
}

/**
 * Whether a page-level handler may act on this keydown: false for a caret key that goes to a text field, which owns it
 * (the browser, or the drawn field behind its hidden input, moves the caret) -- the handler must neither act nor
 * preventDefault.
 */
export function pageMayTakeKey(event: Pick<KeyboardEvent, 'key' | 'target'>): boolean {
  return !(isCaretKey(event.key) && isTextEntryTarget(event.target));
}
